import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrandLogo } from './BrandLogo';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { SignalDto } from '@intelimove/contracts';
import { ROLE_LABEL, useAuth } from '../auth/AuthContext';
import { signalsAdapter } from '../data/adapters';
import { contextAdapter, type ContextStatusDto } from '../data/context';
import { Icon, type IconName } from './Icon';
import { useToast } from './Toast';
import { initials } from './Widgets';
import { isDedicatedAppHost } from '../lib/surface';

const NAV: Array<{ group: string; items: Array<{ to: string; label: string; icon: IconName; badge?: boolean }> }> = [
  { group: 'Inteligência', items: [
    { to: '/', label: 'Visão geral', icon: 'overview' },
    { to: '/parecer', label: 'Parecer executivo', icon: 'moves' },
    { to: '/decisoes', label: 'Central de decisões', icon: 'opportunities' },
    { to: '/cenarios', label: 'Cenários', icon: 'expansion' },
    { to: '/aprendizado', label: 'Resultados e aprendizado', icon: 'sizing' },
    { to: '/mercado', label: 'Concorrentes e mercado', icon: 'market' },
    { to: '/segmentacao', label: 'Segmentação', icon: 'expansion' },
    { to: '/tam-sam-som', label: 'TAM, SAM e SOM', icon: 'sizing' },
    { to: '/pestel-porter', label: 'PESTEL e Cinco Forças', icon: 'environment' },
    { to: '/battlecards', label: 'Battlecards', icon: 'moves' },
    { to: '/portfolio', label: 'Portfólio e Ecossistema', icon: 'opportunities' },
    { to: '/empresas-alvo', label: 'Empresas-alvo', icon: 'accounts' },
    { to: '/pessoas', label: 'Pessoas e decisores', icon: 'person' },
    { to: '/sinais', label: 'Sinais de mercado', icon: 'signals', badge: true },
  ] },
  { group: 'Monitoramento', items: [
    { to: '/monitoramento', label: 'Monitoramentos', icon: 'moves' },
    { to: '/fontes', label: 'Fontes', icon: 'sources' },
  ] },
  { group: 'Gestão', items: [
    { to: '/agendas', label: 'Agendas e entregas', icon: 'opportunities' },
    { to: '/configuracoes', label: 'Configurações', icon: 'settings' },
  ] },
];

export function OrganizationSwitcher() {
  const { me, switchOrganization } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const orgs = me?.organizations ?? [];
  const active = me?.activeOrganization;

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    ref.current?.querySelector<HTMLButtonElement>('.org-menu button[aria-selected=true], .org-menu button')?.focus();
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = async (id: string) => {
    setOpen(false);
    if (id === active?.id) return;
    setBusy(true);
    try {
      await switchOrganization(id);
      navigate('/');
      toast('organização ativa alterada.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="workspace" ref={ref} onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}>
      <button type="button" className="workspace-btn" aria-haspopup="listbox" aria-expanded={open} disabled={busy}
        onClick={() => setOpen((o) => !o)} aria-label={`Organização ativa: ${active?.name ?? 'nenhuma'}${orgs.length > 1 ? '. Alterar organização' : ''}`}>
        <span><small>Organização</small><strong>{busy ? 'Alternando…' : active?.name ?? 'Sem organização'}</strong></span>
        <Icon name="chevron" />
      </button>
      {open && (
        <ul className="org-menu" role="listbox" aria-label="Organizações disponíveis">
          {orgs.map((o) => (
            <li key={o.id}>
              <button type="button" role="option" aria-selected={o.id === active?.id} onClick={() => choose(o.id)}>
                {o.name}<small>{ROLE_LABEL[o.role]}</small>
              </button>
            </li>
          ))}
          <li><button type="button" role="option" aria-selected={false} onClick={() => { setOpen(false); navigate('/empresas/nova'); }}>+ Nova empresa<small>Outra empresa contratante</small></button></li>
          {/* IM-UX-163: só para quem tem papel na plataforma; leva à entrada (com segundo fator), não troca nada sozinho. */}
          {me?.platform && !isDedicatedAppHost() && <li><button type="button" role="option" aria-selected={false} onClick={() => { setOpen(false); navigate('/admin/plataforma'); }}>Administração da plataforma<small>Contexto separado, com segundo fator</small></button></li>}
        </ul>
      )}
    </div>
  );
}

/** IM-CTX-REQ-026: aviso não bloqueante enquanto o contexto da empresa não está pronto. */
export function ContextBanner({ orgId }: { orgId: string }) {
  const { me } = useAuth();
  const { pathname } = useLocation();
  const [s, setS] = useState<ContextStatusDto | null>(null);
  useEffect(() => {
    let alive = true;
    setS(null);
    contextAdapter.status().then((x) => alive && setS(x)).catch(() => undefined);
    return () => { alive = false; };
  }, [orgId, pathname]);
  if (!s || pathname.startsWith('/configuracoes/contexto') || (s.status === 'READY' && !s.pendingChanges)) return null;
  const admin = me?.activeOrganization?.role !== 'MEMBER';
  // IM-EXT-006: o link leva direto à ação necessária (informar o site, revisar, tentar de novo ou confirmar).
  const incomplete = `Contexto da empresa incompleto (${s.readiness.done}/${s.readiness.total}).`;
  // IM-EXT-008-C01: falta só o ICP → mensagem específica e link direto para a aba ICPs (não "contexto incompleto").
  const onlyIcp = s.fit && !s.fit.ready && s.fit.blocker === 'NO_CONFIRMED_ICP' && s.fit.contextFilled && s.status !== 'ENRICHING';
  if (onlyIcp) return <div className="context-banner" role="status">{s.fit!.contextConfirmed ? 'O contexto está confirmado, mas nenhum ICP foi confirmado.' : 'O contexto está preenchido, mas nenhum ICP foi confirmado.'} {admin ? <Link to="/configuracoes/contexto/icps">Revisar ICPs</Link> : <span>Peça a um administrador para confirmar.</span>}</div>;
  const [msg, focus] = s.status === 'ENRICHING' ? ['Estamos pesquisando a empresa para sugerir o contexto.', 'site']
    : !s.primaryDomain && !s.latestRunStatus ? [`${incomplete} Informe o site para o InteliMove pesquisar a empresa.`, 'site']
    : s.latestRunStatus === 'FAILED' && s.status !== 'READY' ? [`${incomplete} A última pesquisa da empresa não foi concluída.`, 'tentar']
    : s.pendingFindings > 0 || s.readiness.coveredBySuggestions ? [s.status === 'READY' ? 'Há novas sugestões para o contexto da empresa.' : `${incomplete} Há sugestões da pesquisa aguardando sua revisão.`, 'revisar']
    : s.status === 'READY' ? [`Há ${s.semanticChanges && s.semanticChanges > 1 ? `${s.semanticChanges} alterações` : 'alterações'} no contexto da empresa ainda não confirmadas.`, 'alteracoes']
    : s.status === 'STALE' ? ['O contexto da empresa foi marcado para revisão.', 'confirmar']
    : !s.latestRunStatus ? [`${incomplete} Pesquise a empresa para receber sugestões.`, 'site']
    : s.readiness.ready ? ['O contexto da empresa está completo e falta confirmar a primeira versão.', 'confirmar']
    : [`${incomplete} As pesquisas de mercado dependem dele.`, 'confirmar'];
  // IM-XPR-005: o texto do link diz a ação certa — "Completar contexto" só quando falta mínimo obrigatório.
  const cta = focus === 'alteracoes' ? 'Revisar alterações' : focus === 'revisar' ? 'Revisar sugestões' : s.readiness.ready ? (focus === 'site' ? 'Ver pesquisa' : 'Revisar contexto') : 'Completar contexto';
  return <div className="context-banner" role="status">{msg} {admin ? <Link to={`/configuracoes/contexto/visao-geral?foco=${focus}`}>{cta}</Link> : <span>Peça a um administrador para {s.readiness.ready ? 'revisar' : 'completar'}.</span>}</div>;
}

export type SearchResult = { kind: 'Sinal' | 'Conta' | 'Análise' | 'Objetivo'; label: string; to: string };
type Result = SearchResult;

/** `extra`: resultados adicionais da nova experiência (objetivos e análises), antes dos sinais (IM-UX-116). */
export function GlobalSearch({ extra, placeholder = 'Buscar empresa, sinal ou oportunidade' }: { extra?: (q: string) => SearchResult[]; placeholder?: string } = {}) {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const [signals, setSignals] = useState<SignalDto[] | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const orgId = me?.activeOrganization?.id;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); input.current?.focus(); input.current?.select(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { setSignals(null); }, [orgId]);

  const ensureIndex = useCallback(() => {
    if (signals !== null) return;
    setSignals([]);
    signalsAdapter.list({ limit: 100 }).then((p) => setSignals(p.items)).catch(() => setSignals([]));
  }, [signals]);

  const results = useMemo<Result[]>(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    const fromSignals = (signals ?? []).filter((s) => `${s.accountName} ${s.title}`.toLowerCase().includes(term))
      .slice(0, 5).map((s) => ({ kind: 'Sinal' as const, label: s.title, to: `/sinais?busca=${encodeURIComponent(s.accountName)}` }));
    // IM-EXT-011: a busca só consulta dados reais do tenant (as contas fictícias fixas do protótipo foram removidas).
    return [...(extra?.(q) ?? []), ...fromSignals].slice(0, 9);
  }, [q, signals, extra]);

  const go = (r: Result) => { setOpen(false); setQ(''); navigate(r.to); };

  return (
    <div className="search" role="search">
      <Icon name="search" />
      <label htmlFor="globalSearch" className="sr-only">{placeholder}</label>
      <input id="globalSearch" ref={input} placeholder={placeholder} autoComplete="off" value={q}
        role="combobox" aria-expanded={open && q.trim().length >= 2} aria-controls="search-results" aria-autocomplete="list"
        onFocus={() => { ensureIndex(); setOpen(true); }} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value); setSel(0); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
          if (e.key === 'Enter' && results[sel]) go(results[sel]!);
          if (e.key === 'Escape') { setOpen(false); input.current?.blur(); }
        }} />
      <span className="shortcut" aria-hidden="true">Ctrl K</span>
      {open && q.trim().length >= 2 && (
        <ul className="search-results" id="search-results" role="listbox" aria-label="Resultados da busca">
          {results.length === 0 && <li className="none">Nenhum resultado para “{q}”.</li>}
          {results.map((r, i) => (
            <li key={`${r.kind}-${r.label}`} role="option" aria-selected={i === sel} onMouseDown={(e) => { e.preventDefault(); go(r); }}>
              <span>{r.label}</span><span className="kind">{r.kind}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AppShell() {
  const { me, logout } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [hot, setHot] = useState<number | null>(null);
  const sidebar = useRef<HTMLElement>(null);
  const menuBtn = useRef<HTMLButtonElement>(null);
  const orgId = me?.activeOrganization?.id ?? 'none';

  useEffect(() => { setDrawer(false); }, [location.pathname]);
  useEffect(() => {
    let alive = true;
    setHot(null);
    signalsAdapter.summary().then((s) => alive && setHot(s.highPriority)).catch(() => undefined);
    return () => { alive = false; };
  }, [orgId]);
  useEffect(() => {
    if (!drawer) return;
    sidebar.current?.querySelector<HTMLElement>('button:not([disabled]), a[href]')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setDrawer(false); menuBtn.current?.focus(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer]);

  const user = me?.user;
  const role = me?.activeOrganization?.role;

  return (
    <div className="app">
      <a href="#conteudo" className="skip-link">Pular para o conteúdo</a>
      <aside className={`sidebar ${drawer ? 'open' : ''}`} id="sidebar" ref={sidebar} aria-label="Navegação principal">
        <BrandLogo variant="sidebar" />
        <OrganizationSwitcher />
        <nav className="nav">
          {NAV.map((g) => (
            <div key={g.group} style={{ display: 'contents' }}>
              <span className="nav-label">{g.group}</span>
              {g.items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.to === '/'}>
                  <Icon name={i.icon} />{i.label}
                  {i.badge && hot ? <span className="badge" aria-label={`${hot} sinais de alta prioridade`}>{hot}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="side-foot">
          <div className="avatar" aria-hidden="true">{user ? initials(user.name) : ''}</div>
          <div className="who"><strong>{user?.name}</strong><small>{role ? ROLE_LABEL[role] : 'Sem organização'}</small></div>
          <button type="button" className="logout" onClick={logout} aria-label="Sair da InteliMove" title="Sair"><Icon name="logout" /></button>
        </div>
      </aside>
      {drawer && <button type="button" className="scrim" aria-label="Fechar menu" onClick={() => setDrawer(false)} />}
      <main className="main">
        <header className="topbar">
          <button ref={menuBtn} type="button" className="icon-btn mobile-menu" aria-label={drawer ? 'Fechar menu' : 'Abrir menu'} aria-expanded={drawer} aria-controls="sidebar" onClick={() => setDrawer((d) => !d)}><Icon name="menu" /></button>
          <GlobalSearch />
          <div className="top-actions">
            <button type="button" className="period" onClick={() => toast('filtro de período disponível na próxima missão.')}>Últimos 30 dias</button>
            <button type="button" className="icon-btn" aria-label="Notificações" onClick={() => toast('nenhuma notificação nova.')}><Icon name="bell" /></button>
          </div>
        </header>
        <div className="content" id="conteudo" tabIndex={-1}>
          {/* key por organização: troca de tenant remonta as telas e descarta qualquer estado/cache anterior. */}
          {/* IM-EXT-011: organização de demonstração ou técnica tem marcação visual permanente. */}
          {me?.activeOrganization?.purpose && me.activeOrganization.purpose !== 'REAL' && (
            <p className="notice-inline org-purpose" role="note"><b>{me.activeOrganization.purpose === 'DEMONSTRACAO' ? 'Organização de demonstração' : 'Organização técnica de teste'}.</b> Os dados desta organização podem ser fictícios ou de teste e não representam inteligência comercial real.</p>
          )}
          {orgId !== 'none' && <ContextBanner orgId={orgId} />}
          <Outlet key={orgId} />
        </div>
      </main>
    </div>
  );
}
